import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import TextAreaField from '../../../components/ui/TextAreaField';
import ToggleSwitch from '../../../components/ui/ToggleSwitch';
import RichTextEditor from '../../../components/ui/RichTextEditor';
import {
  TEACHER_TRAINING_LOBBY_PATH,
  teacherTrainingHostPath,
  teacherTrainingProgramPath,
} from '../../../config/contentRoutes';
import { FEATURE_TRAINING_INTERNAL_LINKS } from '../../../config/featureFlags';
import {
  deleteTrainingProgram,
  formatTrainingHostError,
  getTrainingHostByOwnerUid,
  getTrainingProgramById,
  isValidProgramSlug,
  normalizeProgramSlug,
  updateTrainingProgram,
} from '../../../utils/trainingHost';
import type { TrainingProgramLinkType, TrainingProgramStatus } from '../../../types/trainingHost';
import { DEFAULT_TRAINING_CONTENT_HTML } from '../../../utils/trainingHtml';

const TrainingProgramEditor: React.FC = () => {
  const { programId = '' } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [hostSlug, setHostSlug] = useState('');
  const [isLinkMode, setIsLinkMode] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [programSlug, setProgramSlug] = useState('');
  const [contentHtml, setContentHtml] = useState(DEFAULT_TRAINING_CONTENT_HTML);
  const [linkPath, setLinkPath] = useState('');
  const [linkType, setLinkType] = useState<TrainingProgramLinkType>('internal');
  const [status, setStatus] = useState<TrainingProgramStatus>('upcoming');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!programId || !currentUser?.uid) return;

    const load = async () => {
      try {
        const [host, program] = await Promise.all([
          getTrainingHostByOwnerUid(currentUser.uid),
          getTrainingProgramById(programId),
        ]);

        if (!program || program.ownerUid !== currentUser.uid) {
          toast.error('연수를 찾을 수 없거나 수정 권한이 없습니다.');
          navigate(TEACHER_TRAINING_LOBBY_PATH);
          return;
        }

        if (host) setHostSlug(host.publicSlug);
        setIsLinkMode(program.contentType === 'legacy');
        setTitle(program.title);
        setDescription(program.description ?? '');
        setProgramSlug(program.programSlug ?? '');
        setContentHtml(program.contentHtml || DEFAULT_TRAINING_CONTENT_HTML);
        setLinkPath(program.path ?? '');
        setLinkType(program.linkType ?? 'internal');
        setStatus(program.status);
        setDate(program.date ?? '');
        setCategory(program.category ?? '');
        setIsPublic(program.isPublic);
      } catch (err) {
        console.error(err);
        toast.error(formatTrainingHostError(err));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [programId, currentUser?.uid, navigate]);

  const normalizedProgramSlug = normalizeProgramSlug(programSlug);

  const handleSave = async () => {
    if (!currentUser?.uid || !title.trim()) {
      toast.error('제목을 입력해 주세요.');
      return;
    }

    if (isLinkMode) {
      if (linkType === 'internal' && !FEATURE_TRAINING_INTERNAL_LINKS) {
        toast.error('내부 페이지 연결은 더 이상 지원하지 않습니다. 웹 에디터 연수를 이용해 주세요.');
        return;
      }
      const trimmedPath = linkPath.trim();
      if (!trimmedPath) {
        toast.error('연결할 링크를 입력해 주세요.');
        return;
      }
      if (linkType === 'external' && !/^https?:\/\//i.test(trimmedPath)) {
        toast.error('외부 링크는 http:// 또는 https:// 로 시작해야 합니다.');
        return;
      }
      if (linkType === 'internal' && !trimmedPath.startsWith('/')) {
        toast.error('내부 경로는 / 로 시작해야 합니다.');
        return;
      }
    } else if (!isValidProgramSlug(normalizedProgramSlug)) {
      toast.error('연수 주소(slug) 형식을 확인해 주세요.');
      return;
    }

    setSaving(true);
    try {
      if (isLinkMode) {
        await updateTrainingProgram(programId, currentUser.uid, {
          title: title.trim(),
          description: description.trim() || undefined,
          contentType: 'legacy',
          path: linkPath.trim(),
          linkType,
          status,
          date: date || undefined,
          category: category.trim() || undefined,
          isPublic,
        });
      } else {
        await updateTrainingProgram(programId, currentUser.uid, {
          title: title.trim(),
          description: description.trim() || undefined,
          programSlug: normalizedProgramSlug,
          contentType: 'editor',
          contentHtml,
          status,
          date: date || undefined,
          category: category.trim() || undefined,
          isPublic,
          linkType: 'internal',
        });
      }
      toast.success('연수가 저장되었습니다.');
    } catch (err) {
      console.error(err);
      toast.error(formatTrainingHostError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentUser?.uid) return;
    if (!window.confirm('이 연수를 삭제할까요?')) return;
    try {
      await deleteTrainingProgram(programId, currentUser.uid);
      toast.success('삭제되었습니다.');
      navigate(TEACHER_TRAINING_LOBBY_PATH);
    } catch (err) {
      console.error(err);
      toast.error(formatTrainingHostError(err));
    }
  };

  const openLinkPreview = () => {
    const trimmedPath = linkPath.trim();
    if (!trimmedPath) return;
    if (linkType === 'external') {
      window.open(trimmedPath, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(trimmedPath);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', fontFamily: 'var(--font-family-primary)' }}>
        <p style={{ color: 'var(--Color-text-normal)' }}>불러오는 중…</p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        padding: '32px 24px 48px',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outline" size="medium" onClick={() => navigate(TEACHER_TRAINING_LOBBY_PATH)}>
            ← 로비
          </Button>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {isLinkMode ? (
              linkPath.trim() && (
                <Button variant="outline" size="medium" onClick={openLinkPreview}>
                  링크 열기
                </Button>
              )
            ) : (
              hostSlug &&
              isValidProgramSlug(normalizedProgramSlug) && (
                <Button
                  variant="outline"
                  size="medium"
                  onClick={() => navigate(teacherTrainingProgramPath(hostSlug, normalizedProgramSlug))}
                >
                  미리보기
                </Button>
              )
            )}
            <Button variant="primary" size="medium" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중…' : '저장'}
            </Button>
          </div>
        </div>

        <div>
          <div
            style={{
              display: 'inline-block',
              marginBottom: '8px',
              padding: '4px 10px',
              borderRadius: '999px',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--Color-text-normal)',
              background: 'var(--Color-fill-hover)',
            }}
          >
            {isLinkMode ? '링크 편집' : '에디터 편집'}
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: '26px', fontWeight: 700, color: 'var(--Color-text-strong)' }}>
            {isLinkMode ? '연수 링크 편집' : '연수 페이지 편집'}
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
            {isLinkMode
              ? '등록된 연수 링크와 목록 정보를 수정합니다.'
              : '등록된 연수 페이지 본문과 목록 정보를 수정합니다.'}
          </p>
        </div>

        <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)" width="100%" style={{ boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <InputField label="연수 제목" value={title} onChange={setTitle} width="100%" />
            <TextAreaField label="요약 (로비 목록용)" value={description} onChange={setDescription} />
            {!isLinkMode && (
              <div>
                <InputField
                  label="연수 주소 (slug)"
                  value={programSlug}
                  onChange={setProgramSlug}
                  placeholder="3d-printing-2026"
                  width="100%"
                />
                {hostSlug && (
                  <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                    공개 URL: /teacher-training/h/{hostSlug}/p/{normalizedProgramSlug || 'slug'}
                  </p>
                )}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <InputField label="일정" value={date} onChange={setDate} type="date" width="200px" />
              <InputField label="분야" value={category} onChange={setCategory} width="200px" placeholder="AI, 3D프린팅" />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>상태</span>
              {(['upcoming', 'active', 'completed'] as TrainingProgramStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: status === s ? '2px solid var(--Color-fill-brand-color)' : '1px solid var(--Color-line-normal)',
                    background: status === s ? 'var(--Color-fill-hover)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  {s === 'upcoming' ? '예정' : s === 'active' ? '진행 중' : '완료'}
                </button>
              ))}
            </div>
            <ToggleSwitch label="공개" checked={isPublic} onChange={setIsPublic} />
          </div>
        </Card>

        {isLinkMode ? (
          <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)" width="100%" style={{ boxSizing: 'border-box' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
              연결 링크
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {(FEATURE_TRAINING_INTERNAL_LINKS
                ? (['internal', 'external'] as TrainingProgramLinkType[])
                : (['external'] as TrainingProgramLinkType[])
              ).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setLinkType(type)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: linkType === type ? '2px solid var(--Color-fill-brand-color)' : '1px solid var(--Color-line-normal)',
                    background: linkType === type ? 'var(--Color-fill-hover)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  {type === 'internal' ? '내부 페이지' : '외부 사이트'}
                </button>
              ))}
            </div>
            <InputField
              label={linkType === 'internal' ? '내부 경로' : '외부 URL'}
              value={linkPath}
              onChange={setLinkPath}
              placeholder={linkType === 'internal' ? '/teacher-training/AIEdutech' : 'https://example.com'}
              width="100%"
            />
          </Card>
        ) : (
          <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)" width="100%" alignItems="stretch" style={{ boxSizing: 'border-box' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
              본문
            </h2>
            <RichTextEditor
              value={contentHtml}
              onChange={setContentHtml}
              minHeight="480px"
            />
          </Card>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <Button variant="cancel" size="medium" onClick={handleDelete}>
            삭제
          </Button>
          {hostSlug && (
            <Button variant="outline" size="medium" onClick={() => navigate(teacherTrainingHostPath(hostSlug))}>
              로비로
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingProgramEditor;
